import { PiArchive, PiCheckCircle, PiProhibit } from 'react-icons/pi';
import { FunnelStatusTerminalOutcome } from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useIsElementTruncated } from 'src/modules/shared/hooks/use-is-element-truncated';
import { classes } from 'src/modules/shared/utils/styles.util';
import { Text } from '../text';
import Tooltip from '../tooltips/tooltip';
import type { FunnelStatusNameProps } from './funnel-status-name.types';

/**
 * Renders a status name with its current outcome tint. Icons and the archive
 * explanation or truncated-name tooltip are opt-in per surface.
 * @param props - Status identity and optional presentation details.
 * @returns A tinted label, optionally with an icon and shared tooltip.
 */
export const FunnelStatusName = ({
  name,
  outcome,
  icon = 'none',
  showArchiveTooltip = false,
  showTruncatedNameTooltip = false,
  size = 'small',
  weight = 'regular',
  className,
}: FunnelStatusNameProps) => {
  const { t } = useI18n();
  const { ref, isTruncated } = useIsElementTruncated<HTMLSpanElement>(name);
  const positive = outcome === FunnelStatusTerminalOutcome.POSITIVE;
  const Icon = positive ? PiCheckCircle : PiProhibit;
  const tooltipContent = [
    showTruncatedNameTooltip && isTruncated && name,
    outcome && showArchiveTooltip && t('crm.funnelStatus.archiveExplanation'),
  ]
    .filter(Boolean)
    .join('\n');
  const label = (
    <Text
      as="span"
      size={size}
      weight={weight}
      disableAutoLink
      className={classes(
        'inline-flex min-w-0 items-center gap-2',
        className,
        outcome && (positive ? 'text-success-700' : 'text-secondary-600')
      )}
    >
      <span
        ref={showTruncatedNameTooltip ? ref : undefined}
        className="min-w-0 truncate"
      >
        {name}
      </span>
      {outcome && icon === 'outcome' && (
        <span
          role="img"
          aria-label={t(
            positive
              ? 'crm.funnelStatus.terminalPositive'
              : 'crm.funnelStatus.terminalNegative'
          )}
          className="inline-flex shrink-0"
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
      {outcome && icon === 'archive' && (
        <PiArchive className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
    </Text>
  );
  return tooltipContent ? (
    <Tooltip
      content={tooltipContent}
      as="span"
      size="sm"
      className="max-w-xs whitespace-pre-line text-center"
      contentClassName="min-w-0"
    >
      {label}
    </Tooltip>
  ) : (
    label
  );
};
